import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import ENV_VARS_CONSTANTS from '@packages/env-vars/envVars.json';

import { BaseError, canIgnoreStripeErrors } from '@packages/utils';

import '@sentry/core';
import { ScopeContext } from '@sentry/core';
import * as Sentry from '@sentry/react';

import { getAnonymousId } from './analytics/analytics';

const SENTRY_DSN =
  'https://ab1a8e47337647209ed8f22b1ff18478@o347231.ingest.sentry.io/4504099426074624';

export const initSentry = async () => {
  Sentry.init({
    release: `${import.meta.env.VITE_ENV}:${
      ENV_VARS_CONSTANTS.APP_VERSION?.[import.meta.env.VITE_ENV]
    }`,
    dsn: SENTRY_DSN,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    environment: import.meta.env.VITE_ENV,
    tracesSampleRate: 1.0,
    tracePropagationTargets: [
      /^https:\/\/rest\.freely\.me/,
      /^https:\/\/rest-staging\.staging\.freely\.me/,
    ],
    enabled: checkSentryEnabled(),
  });

  Sentry.setTags({
    anonymousId: (await getAnonymousId()) ?? '',
    STAGE: import.meta.env.VITE_ENV,
    version: ENV_VARS_CONSTANTS.APP_VERSION?.[import.meta.env.VITE_ENV],
  });
};

const isDev = import.meta.env.VITE_ENV === 'dev';
export const checkSentryEnabled = () => {
  if (!isDev) {
    return true;
  }

  try {
    const isSentryEnabled = localStorage.getItem('IS_SENTRY_ENABLED');
    return isSentryEnabled === 'true';
  } catch {
    // do nothing
  }

  return false;
};

export const toggleSentryEnabled = () => {
  try {
    const isEnabled = checkSentryEnabled();
    // eslint-disable-next-line
    // @ts-ignore
    localStorage.setItem('IS_SENTRY_ENABLED', isEnabled ? 'false' : 'true');
  } catch {
    // do nothing
  }
};

type DomainType =
  | 'destinations'
  | 'featureFlags'
  | 'remoteConfig'
  | 'boost'
  | 'trip'
  | 'quote'
  | 'emcLetter'
  | 'promoCode'
  | 'guest'
  | 'emailQuote'
  | 'emcScreeningResults'
  | 'paymentIntent';

type EffectEvent = 'init' | 'subscribe';

type UserAction =
  | 'fetch'
  | 'create'
  | 'delete'
  | 'purchase'
  | 'submit'
  | 'add'
  | 'remove'
  | 'toggle'
  | 'update'
  | 'refresh'
  | 'cancel'
  | 'revert'
  | 'confirm';

type WebEvent = `${UserAction}.${DomainType}` | `effect.${EffectEvent}` | 'missing.translation';

export const sentryTags = ({ source, event }: { source: string; event: WebEvent }) => {
  Sentry.getCurrentScope()?.setTags({
    source,
    event,
  });
};

export type CaptureExceptionArgs = Parameters<typeof Sentry.captureException>;

export const captureFreelyError = (
  error: CaptureExceptionArgs[0],
  properties?: CaptureExceptionArgs[1],
) => {
  /**
   * First ignores Stripe Input errors
   * Ie wrong card number, wrong expiry date or Wallet Cancellation
   */
  if (canIgnoreStripeErrors(error)) {
    return;
  }

  /**
   * Then check if the error is a BaseError and add the details to the extra context
   */

  if (error instanceof BaseError) {
    const context = {
      ...(properties ?? {}),
      extra: {
        ...((properties as ScopeContext)?.extra ?? {}),
        ...(error?.details ?? {}),
      },
    } as CaptureExceptionArgs[1];

    return Sentry.captureException(error, context);
  }

  /**
   * If the error is not a BaseError, we can still add the extra context
   */
  return Sentry.captureException(error, properties);
};
